// opervex-frontend/src/pages/Others/KardexComparison.js

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Form, Modal, ProgressBar, Spinner, Table } from "react-bootstrap";

import FileSelectorService from "../../services/FileSelectorService";
import { useAuth } from "../../contexts/AuthContext";

import * as XLSX from "xlsx";

const rolePageRequire = "KARDEX_COMPARISON";

// =========================================================
// STATUS DA BIPAGEM
// =========================================================

const SCAN_STATUS = {
  FOUND: "FOUND",
  NOT_FOUND: "NOT_FOUND",
};

const KardexComparison = () => {
  /* ----- Screen Control -----*/
  const { roles, loadingGlobal } = useAuth();
  const [disabledField, setDisabledField] = useState(true);

  /* ----- KARDEX -----*/
  const [kardexData, setKardexData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ----- BIPAGEM -----*/
  const [showProductModal, setShowProductModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showDivergencesModal, setShowDivergencesModal] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState(null);

  const [scannedData, setScannedData] = useState([]);
  const [pendingItem, setPendingItem] = useState(null);

  const [scanValue, setScanValue] = useState("");
  const [scanError, setScanError] = useState("");
  const [lastScan, setLastScan] = useState(null);

  const scanInputRef = useRef(null);

  const fetchData = async () => {
    checkAccess();
  };

  useEffect(() => {
    fetchData();
  });

  const checkAccess = () => {
    const hasPermission = roles.find((role) => role.command === rolePageRequire);

    if (!hasPermission) {
      setDisabledField(true);
      return;
    }

    setDisabledField(hasPermission.actions.some((action) => action.key === "Read"));
  };

  // =========================================================
  // NORMALIZAÇÃO
  // =========================================================

  /*
   * Normaliza Serial/MAC para comparação.
   *
   * Exemplos:
   *
   * 00:11:22:33:44:55
   * 00-11-22-33-44-55
   * 001122334455
   *
   * Todos ficam:
   *
   * 001122334455
   */
  const normalizeValue = (value) => {
    if (value === undefined || value === null) {
      return "";
    }

    return String(value)
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");
  };

  // =========================================================
  // PRODUTOS DO KARDEX
  // =========================================================

  const products = useMemo(() => {
    return Object.values(
      kardexData.reduce((acc, item) => {
        if (!item.externalCode) {
          return acc;
        }

        if (!acc[item.externalCode]) {
          acc[item.externalCode] = {
            externalCode: item.externalCode,
            description: item.description,
            quantity: 0,
          };
        }

        acc[item.externalCode].quantity += 1;

        return acc;
      }, {}),
    );
  }, [kardexData]);

  // =========================================================
  // ITENS DO PRODUTO SELECIONADO
  // =========================================================

  const selectedProductKardexItems = useMemo(() => {
    if (!selectedProduct) {
      return [];
    }

    return kardexData.filter((item) => item.externalCode === selectedProduct.externalCode);
  }, [kardexData, selectedProduct]);

  // =========================================================
  // TODOS OS ITENS BIPADOS DURANTE O PRODUTO SELECIONADO
  // =========================================================

  const selectedProductScannedItems = useMemo(() => {
    if (!selectedProduct) {
      return [];
    }

    return scannedData.filter((item) => item.scannedForExternalCode === selectedProduct.externalCode);
  }, [scannedData, selectedProduct]);

  // =========================================================
  // EQUIPAMENTOS ENCONTRADOS / CONFERIDOS
  // =========================================================

  const selectedProductFoundItems = useMemo(() => {
    return selectedProductScannedItems.filter((item) => item.status === SCAN_STATUS.FOUND);
  }, [selectedProductScannedItems]);

  // =========================================================
  // EQUIPAMENTOS NÃO ENCONTRADOS
  // =========================================================

  const selectedProductNotFoundItems = useMemo(() => {
    return selectedProductScannedItems.filter((item) => item.status === SCAN_STATUS.NOT_FOUND);
  }, [selectedProductScannedItems]);

  // =========================================================
  // TOTAL GERAL ENCONTRADO
  // =========================================================

  const totalFound = useMemo(() => {
    return scannedData.filter((item) => item.status === SCAN_STATUS.FOUND).length;
  }, [scannedData]);

  // =========================================================
  // TOTAL GERAL NÃO ENCONTRADO
  // =========================================================

  const totalNotFound = useMemo(() => {
    return scannedData.filter((item) => item.status === SCAN_STATUS.NOT_FOUND).length;
  }, [scannedData]);

  // =========================================================
  // DIVERGÊNCIAS
  // =========================================================

  const unscannedKardexItems = useMemo(() => {
    return kardexData.filter(
      (kardexItem) =>
        !scannedData.some(
          (scannedItem) =>
            scannedItem.status === SCAN_STATUS.FOUND &&
            normalizeValue(scannedItem.scannedSerial) === normalizeValue(kardexItem.serialNumber),
        ),
    );
  }, [kardexData, scannedData]);

  const notFoundScannedItems = useMemo(() => {
    return scannedData.filter((item) => item.status === SCAN_STATUS.NOT_FOUND);
  }, [scannedData]);

  // =========================================================
  // PROGRESSO
  //
  // IMPORTANTE:
  //
  // Produto não encontrado NÃO entra no progresso.
  // =========================================================

  const scanProgress = useMemo(() => {
    if (!selectedProduct?.quantity) {
      return 0;
    }

    return Math.min(100, (selectedProductFoundItems.length / selectedProduct.quantity) * 100);
  }, [selectedProduct, selectedProductFoundItems]);

  // =========================================================
  // FOCO AUTOMÁTICO
  // =========================================================

  const focusScanInput = () => {
    setTimeout(() => {
      scanInputRef.current?.focus();
    }, 50);
  };

  useEffect(() => {
    if (showScanModal) {
      focusScanInput();
    }
  }, [showScanModal, pendingItem]);

  // Hooks must run before an early return so their call order is stable.
  if (loadingGlobal) {
    return <div>Carregando...</div>;
  }

  // =========================================================
  // IMPORTAR KARDEX
  // =========================================================
  const handleImportKardex = async () => {
    try {
      setError("");

      const kardexData = await getKardexData();

      setLoading(true);

      try {
        const productsMap = Object.fromEntries(kardexData.products.map((product) => [product.product, product.description]));

        const data = (kardexData.serials || [])
          .map((x, index) => ({
            id: index + 1,
            externalCode: String(x.product ?? "").trim(),
            description: String(productsMap[x.product] ?? "").trim(),
            serialNumber: String(x.serial ?? "").trim(),
            mac: String(x.mac ?? "").trim(),
          }))
          .filter((item) => item.externalCode || item.description || item.serialNumber || item.mac);

        setKardexData(data);
        setFileName(kardexData.filename || "");

        /*
         * Novo Kardex = nova conferência.
         */
        setSelectedProduct(null);
        setScannedData([]);
        setPendingItem(null);
        setLastScan(null);

        setScanValue("");
        setScanError("");

        if (!data.length) {
          setError("O arquivo foi importado, mas nenhum registro válido foi encontrado.");
        }
      } catch (err) {
        console.error("Erro ao processar Kardex:", err);

        setError("Não foi possível processar os dados do arquivo Kardex.");

        setKardexData([]);
        setFileName("");
      } finally {
        setLoading(false);
      }
    } catch (err) {
      console.error("Erro ao selecionar Kardex:", err);

      setError("Não foi possível selecionar o arquivo Kardex.");

      setLoading(false);
    }
  };

  // =========================================================
  // ABRIR SELEÇÃO DE PRODUTO
  // =========================================================

  const handleOpenBipagem = () => {
    setError("");

    if (!kardexData.length) {
      setError("Importe um arquivo Kardex antes de iniciar a bipagem.");

      return;
    }

    setShowProductModal(true);
  };

  const getKardexData = async () => {
    const fileKardex = await FileSelectorService.selectFile({ accept: ".xlsx" });
    if (!fileKardex) return;

    const buffer = await fileKardex.arrayBuffer();

    const workbook = XLSX.read(buffer, { type: "array" });

    const products = XLSX.utils
      .sheet_to_json(workbook.Sheets["1-Produtos"], {
        range: 1,
      })
      .map((item) => ({
        product: item["Cod Produto"],
        deposit: item.Armaz,
        description: item.Descricao,
        unit: item.Unid,
        quantity: Number(String(item.Quant).replace(".", "").replace(",", ".")),
      }));

    const serials = XLSX.utils
      .sheet_to_json(workbook.Sheets["2-Seriais"], {
        range: 1,
      })
      .map((item) => ({
        product: item["Cod Produto"],
        serial: item.Serial,
        mac: item.Mac,
        quantity: Number(String(item.Quant).replace(".", "").replace(",", ".")),
      }));

    return {
      products,
      serials,
      filename: fileKardex.name,
    };
  };

  // =========================================================
  // SELECIONAR PRODUTO
  // =========================================================

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);

    setScanValue("");
    setScanError("");
    setLastScan(null);
    setPendingItem(null);

    setShowProductModal(false);
    setShowScanModal(true);
  };

  // =========================================================
  // LOCALIZAR SERIAL NO KARDEX INTEIRO
  //
  // IMPORTANTE:
  //
  // Não procuramos somente no produto selecionado.
  //
  // Isso permite detectar se o Serial pertence
  // a outro tipo de equipamento.
  // =========================================================

  const findKardexItemBySerial = (serialNumber) => {
    const normalizedSerial = normalizeValue(serialNumber);

    if (!normalizedSerial) {
      return null;
    }

    return kardexData.find((item) => normalizeValue(item.serialNumber) === normalizedSerial) || null;
  };

  // =========================================================
  // VERIFICAR SE SERIAL JÁ FOI BIPADO
  // =========================================================

  const findAlreadyScannedBySerial = (serialNumber) => {
    const normalizedSerial = normalizeValue(serialNumber);

    if (!normalizedSerial) {
      return null;
    }

    return scannedData.find((item) => normalizeValue(item.scannedSerial) === normalizedSerial) || null;
  };

  // =========================================================
  // VERIFICAR SE MAC JÁ FOI UTILIZADO
  // =========================================================

  const findAlreadyScannedByMac = (mac) => {
    const normalizedMac = normalizeValue(mac);

    if (!normalizedMac) {
      return null;
    }

    return scannedData.find((item) => item.scannedMac && normalizeValue(item.scannedMac) === normalizedMac) || null;
  };

  // =========================================================
  // CONCLUIR EQUIPAMENTO ENCONTRADO
  // =========================================================

  const completeScan = (kardexItem, scannedSerial, scannedMac = "") => {
    const newScan = {
      id: `${Date.now()}-${Math.random()}`,

      /*
       * Produto real encontrado no Kardex.
       */
      externalCode: kardexItem.externalCode,

      description: kardexItem.description,

      /*
       * Produto que estava sendo conferido
       * no momento da bipagem.
       */
      scannedForExternalCode: selectedProduct.externalCode,

      scannedForDescription: selectedProduct.description,

      /*
       * Dados esperados no Kardex.
       */
      serialNumber: kardexItem.serialNumber,

      mac: kardexItem.mac || "",

      /*
       * Dados efetivamente bipados.
       */
      scannedSerial: scannedSerial,

      scannedMac: scannedMac,

      status: SCAN_STATUS.FOUND,

      foundInKardex: true,

      serialMatched: true,

      macMatched: kardexItem.mac ? normalizeValue(kardexItem.mac) === normalizeValue(scannedMac) : true,
    };

    setScannedData((previous) => [...previous, newScan]);

    setLastScan(newScan);

    setPendingItem(null);

    setScanValue("");
    setScanError("");

    focusScanInput();
  };

  // =========================================================
  // ADICIONAR PRODUTO NÃO ENCONTRADO
  // =========================================================

  const addNotFoundScan = (serialNumber, scannedMac = "") => {
    const newScan = {
      id: `${Date.now()}-${Math.random()}`,

      /*
       * Não sabemos qual é o produto real,
       * pois ele não existe no Kardex.
       */
      externalCode: "",
      description: "",

      /*
       * Guardamos em qual produto ele estava
       * sendo conferido.
       */
      scannedForExternalCode: selectedProduct.externalCode,

      scannedForDescription: selectedProduct.description,

      /*
       * Serial físico bipado.
       */
      serialNumber: serialNumber,

      mac: scannedMac,

      scannedSerial: serialNumber,

      scannedMac,

      status: SCAN_STATUS.NOT_FOUND,

      foundInKardex: false,

      serialMatched: false,

      macMatched: false,
    };

    setScannedData((previous) => [...previous, newScan]);

    setLastScan(newScan);

    setPendingItem(null);

    setScanValue("");
    setScanError("");

    focusScanInput();
  };

  // =========================================================
  // REALIZAR BIPAGEM
  // =========================================================

  const handleScan = () => {
    const value = scanValue.trim();

    setScanError("");

    if (!value) {
      focusScanInput();
      return;
    }

    // =======================================================
    // ETAPA 2
    //
    // AGUARDANDO MAC
    // =======================================================

    if (pendingItem) {
      const scannedMac = normalizeValue(value);

      // -------------------------------------------------------
      // VERIFICA SE MAC JÁ FOI BIPADO
      // -------------------------------------------------------

      const alreadyScannedMac = findAlreadyScannedByMac(value);

      if (alreadyScannedMac) {
        setScanError(`Este MAC já foi utilizado no equipamento de Serial ${alreadyScannedMac.scannedSerial}.`);

        setScanValue("");

        focusScanInput();

        return;
      }

      // -------------------------------------------------------
      // SERIAL NÃO ENCONTRADO
      //
      // Não há MAC de referência no Kardex para validar, mas o
      // MAC ainda deve ser registrado quando o produto o utiliza.
      // -------------------------------------------------------

      if (pendingItem.status === SCAN_STATUS.NOT_FOUND) {
        addNotFoundScan(pendingItem.scannedSerial, value);

        return;
      }

      const expectedMac = normalizeValue(pendingItem.kardexItem.mac);

      // -------------------------------------------------------
      // MAC CORRETO
      // -------------------------------------------------------

      if (expectedMac && expectedMac === scannedMac) {
        completeScan(pendingItem.kardexItem, pendingItem.scannedSerial, value);

        return;
      }

      // -------------------------------------------------------
      // MAC INCORRETO
      // -------------------------------------------------------

      setScanError(
        `O MAC bipado não corresponde ao equipamento de Serial ${pendingItem.kardexItem.serialNumber}. Tente novamente.`,
      );

      setScanValue("");

      focusScanInput();

      return;
    }

    // =======================================================
    // ETAPA 1
    //
    // AGUARDANDO SERIAL
    // =======================================================

    // -------------------------------------------------------
    // VERIFICA SE SERIAL JÁ FOI BIPADO
    // -------------------------------------------------------

    const alreadyScanned = findAlreadyScannedBySerial(value);

    if (alreadyScanned) {
      setScanError(`O Serial ${alreadyScanned.scannedSerial} já foi bipado anteriormente.`);

      setScanValue("");

      focusScanInput();

      return;
    }

    // -------------------------------------------------------
    // PROCURA SERIAL NO KARDEX INTEIRO
    // -------------------------------------------------------

    const kardexItem = findKardexItemBySerial(value);

    // =======================================================
    // CASO 1
    //
    // SERIAL NÃO EXISTE EM NENHUM PRODUTO.
    //
    // Adicionamos com status:
    //
    // PRODUTO NÃO ENCONTRADO
    // =======================================================

    if (!kardexItem) {
      const selectedProductUsesMac = selectedProductKardexItems.some((item) => Boolean(item.mac?.trim()));

      if (selectedProductUsesMac) {
        setPendingItem({
          scannedSerial: value,
          status: SCAN_STATUS.NOT_FOUND,
        });

        setScanValue("");
        setScanError("");
        setLastScan(null);

        return;
      }

      addNotFoundScan(value);

      return;
    }

    // =======================================================
    // CASO 2
    //
    // SERIAL EXISTE, MAS PERTENCE A OUTRO PRODUTO.
    // =======================================================

    if (kardexItem.externalCode !== selectedProduct.externalCode) {
      setScanError(`O Serial "${value}" pertence a outro equipamento: ${kardexItem.externalCode} - ${kardexItem.description}.`);

      setScanValue("");

      focusScanInput();

      return;
    }

    // =======================================================
    // CASO 3
    //
    // SERIAL PERTENCE AO PRODUTO CORRETO.
    // =======================================================

    const hasMac = Boolean(kardexItem.mac?.trim());

    // -------------------------------------------------------
    // POSSUI MAC
    //
    // Solicita MAC antes de concluir.
    // -------------------------------------------------------

    if (hasMac) {
      setPendingItem({
        kardexItem,
        scannedSerial: value,
      });

      setScanValue("");
      setScanError("");
      setLastScan(null);

      focusScanInput();

      return;
    }

    // -------------------------------------------------------
    // NÃO POSSUI MAC
    //
    // Conclui imediatamente.
    // -------------------------------------------------------

    completeScan(kardexItem, value, "");
  };

  // =========================================================
  // ENTER DO LEITOR
  // =========================================================

  const handleScanKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();

      handleScan();
    }
  };

  // =========================================================
  // CANCELAR SERIAL QUE ESTÁ AGUARDANDO MAC
  // =========================================================

  const handleCancelPendingItem = () => {
    setPendingItem(null);

    setScanValue("");
    setScanError("");
    setLastScan(null);

    focusScanInput();
  };

  // =========================================================
  // REMOVER BIPAGEM
  // =========================================================

  const handleRemoveScan = (id) => {
    setScannedData((previous) => previous.filter((item) => item.id !== id));

    setLastScan(null);

    focusScanInput();
  };

  // =========================================================
  // FECHAR MODAL DE BIPAGEM
  // =========================================================

  const handleCloseScanModal = () => {
    setShowScanModal(false);

    setScanValue("");
    setScanError("");
    setLastScan(null);
    setPendingItem(null);
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      <div className="container-fluid py-3">
        <Card>
          {/* ================================================= */}
          {/* CABEÇALHO                                        */}
          {/* ================================================= */}

          <Card.Header>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div>
                <h5 className="mb-0">Comparação de Kardex</h5>

                {fileName && <small className="text-muted">Arquivo importado: {fileName}</small>}
              </div>

              <div className="d-flex gap-2">
                <Button
                  variant="warning"
                  onClick={() => setShowDivergencesModal(true)}
                  disabled={loading || !kardexData.length || !disabledField}
                >
                  Ver divergências
                </Button>

                <Button
                  variant="success"
                  onClick={handleOpenBipagem}
                  disabled={loading || !kardexData.length}
                  hidden={!disabledField}
                >
                  Bipar Equipamentos
                </Button>

                <Button variant="primary" onClick={handleImportKardex} disabled={loading} hidden={!disabledField}>
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Importando...
                    </>
                  ) : (
                    "Importar Kardex"
                  )}
                </Button>
              </div>
            </div>
          </Card.Header>

          {/* ================================================= */}
          {/* CONTEÚDO                                         */}
          {/* ================================================= */}

          <Card.Body>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            {kardexData.length > 0 ? (
              <>
                {/* =========================================== */}
                {/* RESUMO                                     */}
                {/* =========================================== */}

                <div className="d-flex gap-4 mb-3 flex-wrap">
                  <div>
                    <strong>Registros no Kardex:</strong> {kardexData.length.toLocaleString("pt-BR")}
                  </div>

                  <div>
                    <strong>Tipos de produtos:</strong> {products.length.toLocaleString("pt-BR")}
                  </div>

                  <div>
                    <strong>Conferidos:</strong> <span className="text-success">{totalFound.toLocaleString("pt-BR")}</span>
                  </div>

                  <div>
                    <strong>Não encontrados:</strong>{" "}
                    <span className={totalNotFound ? "text-warning" : ""}>{totalNotFound.toLocaleString("pt-BR")}</span>
                  </div>
                </div>

                {/* =========================================== */}
                {/* TABELA DO KARDEX                           */}
                {/* =========================================== */}

                <div
                  className="table-responsive"
                  style={{
                    maxHeight: "calc(100vh - 310px)",
                    overflowY: "auto",
                  }}
                >
                  <Table striped bordered hover size="sm" className="mb-0">
                    <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={{ width: "60px" }}>#</th>
                        <th>Produto</th>
                        <th>Descrição</th>
                        <th>Número de Série</th>
                        <th>MAC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kardexData.map((item, index) => {
                        const scanned = scannedData.find((x) => x.serialNumber === item.serialNumber || x.mac === item.mac);

                        const found = scanned?.status === SCAN_STATUS.FOUND;

                        return (
                          <tr key={item.id} className={found ? "table-success" : ""}>
                            <td>{index + 1}</td>
                            <td>{item.externalCode}</td>
                            <td>{item.description}</td>
                            <td>{item.serialNumber}</td>
                            <td>{item.mac || ""}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </>
            ) : (
              !loading && (
                <div className="text-center text-muted py-5">
                  <div className="mb-2">Nenhum Kardex importado.</div>

                  <div>
                    Clique em <strong>Importar Kardex</strong> para selecionar um arquivo.
                  </div>
                </div>
              )
            )}
          </Card.Body>
        </Card>
      </div>

      {/* ===================================================== */}
      {/* MODAL - DIVERGÊNCIAS                                 */}
      {/* ===================================================== */}

      <Modal show={showDivergencesModal} onHide={() => setShowDivergencesModal(false)} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Itens Divergentes</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <h5 className="mb-3">
            Produtos não bipados <Badge bg="secondary">{unscannedKardexItems.length}</Badge>
          </h5>

          {unscannedKardexItems.length > 0 ? (
            <div className="table-responsive mb-4" style={{ maxHeight: "280px", overflowY: "auto" }}>
              <Table striped bordered hover size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Produto</th>
                    <th>Descrição</th>
                    <th>Número de Série</th>
                    <th>MAC</th>
                  </tr>
                </thead>
                <tbody>
                  {unscannedKardexItems.map((item) => (
                    <tr key={item.id}>
                      <td>{item.externalCode}</td>
                      <td>{item.description}</td>
                      <td>{item.serialNumber}</td>
                      <td>{item.mac || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <Alert variant="success">Todos os produtos do Kardex foram bipados.</Alert>
          )}

          <h5 className="mb-3">
            Produtos não encontrados{" "}
            <Badge bg="warning" text="dark">
              {notFoundScannedItems.length}
            </Badge>
          </h5>

          {notFoundScannedItems.length > 0 ? (
            <div className="table-responsive" style={{ maxHeight: "280px", overflowY: "auto" }}>
              <Table striped bordered hover size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Produto em conferência</th>
                    <th>Número de Série bipado</th>
                    <th>MAC bipado</th>
                  </tr>
                </thead>
                <tbody>
                  {notFoundScannedItems.map((item) => (
                    <tr key={item.id} className="table-warning">
                      <td>
                        {item.scannedForExternalCode} - {item.scannedForDescription}
                      </td>
                      <td>{item.scannedSerial}</td>
                      <td>{item.scannedMac || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <Alert variant="success" className="mb-0">
              Nenhum produto não encontrado foi registrado.
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDivergencesModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===================================================== */}
      {/* MODAL - SELECIONAR PRODUTO                           */}
      {/* ===================================================== */}

      <Modal show={showProductModal} onHide={() => setShowProductModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Selecionar Produto para Bipagem</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="text-muted mb-3">Selecione o tipo de produto que deseja iniciar ou continuar a bipagem física.</p>

          {products.length > 0 ? (
            <div className="list-group">
              {products.map((product) => {
                /*
                 * Somente FOUND conta como
                 * conferência do produto.
                 */
                const found = scannedData.filter(
                  (item) => item.status === SCAN_STATUS.FOUND && item.externalCode === product.externalCode,
                ).length;

                const notFound = scannedData.filter(
                  (item) => item.status === SCAN_STATUS.NOT_FOUND && item.scannedForExternalCode === product.externalCode,
                ).length;

                const completed = found >= product.quantity;

                return (
                  <button
                    key={product.externalCode}
                    type="button"
                    className="list-group-item list-group-item-action"
                    onClick={() => handleSelectProduct(product)}
                  >
                    <div className="d-flex justify-content-between align-items-center gap-3">
                      <div>
                        <div className="fw-bold">{product.externalCode}</div>

                        <div className="text-muted">{product.description}</div>
                      </div>

                      <div className="text-end d-flex align-items-center gap-2">
                        {notFound > 0 && (
                          <Badge bg="warning" text="dark" pill style={{ fontSize: "0.9rem" }}>
                            Não encontrados: {notFound}
                          </Badge>
                        )}

                        <Badge
                          bg={completed ? "success" : "primary"}
                          pill
                          style={{
                            fontSize: "0.9rem",
                          }}
                        >
                          {found} / {product.quantity}
                        </Badge>

                        {completed && <div className="small text-success mt-1">Conferido</div>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <Alert variant="warning" className="mb-0">
              Nenhum produto disponível para bipagem.
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProductModal(false)}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===================================================== */}
      {/* MODAL - BIPAGEM                                      */}
      {/* ===================================================== */}

      <Modal show={showScanModal} onHide={handleCloseScanModal} centered size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Bipagem de Equipamentos</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedProduct && (
            <>
              {/* ============================================= */}
              {/* PRODUTO / CONTADORES                         */}
              {/* ============================================= */}

              <Card className="mb-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <div>
                      <div className="text-muted small">Produto em conferência</div>

                      <div className="fw-bold fs-5">
                        {selectedProduct.externalCode} - {selectedProduct.description}
                      </div>
                    </div>

                    <div className="d-flex gap-4">
                      <div className="text-end">
                        <div className="text-muted small">Conferidos</div>

                        <div className="fs-4 fw-bold text-success">
                          {selectedProductFoundItems.length} / {selectedProduct.quantity}
                        </div>
                      </div>

                      <div className="text-end">
                        <div className="text-muted small">Não encontrados</div>

                        <div className={`fs-4 fw-bold ${selectedProductNotFoundItems.length ? "text-warning" : ""}`}>
                          {selectedProductNotFoundItems.length}
                        </div>
                      </div>
                    </div>
                  </div>

                  <ProgressBar
                    className="mt-3"
                    now={scanProgress}
                    label={`${Math.round(scanProgress)}%`}
                    variant={scanProgress >= 100 ? "success" : "primary"}
                  />
                </Card.Body>
              </Card>

              {/* ============================================= */}
              {/* ÁREA DE BIPAGEM                              */}
              {/* ============================================= */}

              <Card className="mb-3">
                <Card.Body>
                  {!pendingItem ? (
                    /* --------------------------------------- */
                    /* AGUARDANDO SERIAL                       */
                    /* --------------------------------------- */

                    <Alert variant="primary">
                      <div className="fw-bold fs-5">Bipar Número de Série</div>

                      <div>Bipe o número de série do próximo equipamento.</div>
                    </Alert>
                  ) : (
                    /* --------------------------------------- */
                    /* AGUARDANDO MAC                          */
                    /* --------------------------------------- */

                    <Alert variant="warning">
                      <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                        <div>
                          <div className="fw-bold fs-5">
                            {pendingItem.status === SCAN_STATUS.NOT_FOUND
                              ? "Serial não encontrado no Kardex"
                              : "Serial encontrado!"}
                          </div>

                          <div className="mt-2">
                            <strong>Serial:</strong> {pendingItem.scannedSerial}
                          </div>

                          {pendingItem.kardexItem && (
                            <div>
                              <strong>Produto:</strong> {pendingItem.kardexItem.externalCode} - {pendingItem.kardexItem.description}
                            </div>
                          )}

                          <div className="mt-2">
                            {pendingItem.status === SCAN_STATUS.NOT_FOUND ? (
                              "Agora bipe o MAC deste equipamento para registrá-lo na conferência."
                            ) : (
                              <>
                                Agora bipe o <strong>MAC</strong> deste equipamento.
                              </>
                            )}
                          </div>
                        </div>

                        <Button variant="outline-secondary" size="sm" onClick={handleCancelPendingItem}>
                          Cancelar Serial
                        </Button>
                      </div>
                    </Alert>
                  )}

                  {/* ========================================= */}
                  {/* CAMPO DE LEITURA                         */}
                  {/* ========================================= */}

                  <Form.Group>
                    <Form.Label>
                      <strong>{pendingItem ? "Bipar MAC" : "Bipar Número de Série"}</strong>
                    </Form.Label>

                    <div className="d-flex gap-2">
                      <Form.Control
                        ref={scanInputRef}
                        type="text"
                        size="lg"
                        value={scanValue}
                        placeholder={pendingItem ? "Bipe o MAC do equipamento..." : "Bipe o número de série..."}
                        autoComplete="off"
                        onChange={(event) => setScanValue(event.target.value)}
                        onKeyDown={handleScanKeyDown}
                      />

                      <Button variant={pendingItem ? "warning" : "primary"} size="lg" onClick={handleScan}>
                        {pendingItem ? "Validar MAC" : "Validar Serial"}
                      </Button>
                    </div>

                    <Form.Text className="text-muted">O leitor pode enviar Enter automaticamente após a leitura.</Form.Text>
                  </Form.Group>

                  {/* ========================================= */}
                  {/* ERRO / SERIAL DE OUTRO PRODUTO           */}
                  {/* ========================================= */}

                  {scanError && (
                    <Alert variant="danger" className="mt-3 mb-0">
                      <strong>Atenção!</strong>

                      <div>{scanError}</div>
                    </Alert>
                  )}

                  {/* ========================================= */}
                  {/* ÚLTIMO RESULTADO                         */}
                  {/* ========================================= */}

                  {lastScan && !pendingItem && (
                    <Alert variant={lastScan.status === SCAN_STATUS.FOUND ? "success" : "warning"} className="mt-3 mb-0">
                      {lastScan.status === SCAN_STATUS.FOUND ? (
                        <>
                          <strong>✓ Equipamento conferido com sucesso!</strong>

                          <div className="mt-2">
                            <strong>Serial:</strong> {lastScan.serialNumber}
                          </div>

                          <div>
                            <strong>MAC:</strong> {lastScan.mac || "Não possui"}
                          </div>
                        </>
                      ) : (
                        <>
                          <strong>⚠ Produto não encontrado no Kardex</strong>

                          <div className="mt-2">
                            <strong>Serial bipado:</strong> {lastScan.scannedSerial}
                          </div>

                          <div className="mt-1">
                            O Serial foi adicionado à conferência, mas não foi localizado em nenhum produto do Kardex.
                          </div>
                        </>
                      )}
                    </Alert>
                  )}
                </Card.Body>
              </Card>

              {/* ============================================= */}
              {/* EQUIPAMENTOS BIPADOS                         */}
              {/* ============================================= */}

              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <strong>Equipamentos Bipados</strong>

                    <div className="d-flex gap-2">
                      <Badge bg="success">Conferidos: {selectedProductFoundItems.length}</Badge>

                      <Badge bg="warning" text="dark">
                        Não encontrados: {selectedProductNotFoundItems.length}
                      </Badge>
                    </div>
                  </div>
                </Card.Header>

                <div
                  className="table-responsive"
                  style={{
                    maxHeight: "350px",
                    overflowY: "auto",
                  }}
                >
                  <Table striped bordered hover size="sm" className="mb-0">
                    <thead
                      className="table-light"
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      <tr>
                        <th style={{ width: "60px" }}>#</th>
                        <th>Número de Série</th>
                        <th>MAC</th>
                        <th>Situação</th>
                        <th style={{ width: "100px" }}>Ação</th>
                      </tr>
                    </thead>

                    <tbody>
                      {selectedProductScannedItems.length > 0 ? (
                        selectedProductScannedItems.map((item, index) => (
                          <tr key={item.id} className={item.status === SCAN_STATUS.NOT_FOUND ? "table-warning" : ""}>
                            <td>{index + 1}</td>
                            <td>{item.scannedSerial}</td>
                            <td>{item.mac || "-"}</td>
                            <td>
                              {item.status === SCAN_STATUS.FOUND ? (
                                <Badge bg="success">Conferido</Badge>
                              ) : (
                                <Badge bg="warning" text="dark">
                                  Produto não encontrado
                                </Badge>
                              )}
                            </td>
                            <td>
                              <Button variant="outline-danger" size="sm" onClick={() => handleRemoveScan(item.id)}>
                                Remover
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center text-muted py-4">
                            Nenhum equipamento bipado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseScanModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default KardexComparison;
